import type { GlossaryTerm } from '@/api'
import { PageLoader } from '@/components'
import { useGlossaryTerms } from '@/services'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { paths } from '@/routes/paths'

export const IcuGlossaryPage = () => {
  const { patientId } = useParams<{ patientId: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data: terms = [], isLoading } = useGlossaryTerms(patientId!)

  // Aktiver Begriff aus der URL ableiten (Detail-Route /icu-glossary/:termId).
  // Steuert Master-Detail: Desktop zeigt Liste + Detail nebeneinander,
  // Mobile blendet die Liste aus, sobald ein Begriff geöffnet ist.
  const pathParts = pathname.split('/').filter(Boolean)
  const glossaryIdx = pathParts.indexOf(paths.icuGlossary)
  const activeTermId = glossaryIdx >= 0 ? pathParts[glossaryIdx + 1] : undefined

  if (isLoading) return <PageLoader />

  const groupedByLetter = terms.reduce<Record<string, GlossaryTerm[]>>((acc, term) => {
    const letter = (term.term || term.layman_term || '').charAt(0).toUpperCase()
    if (!letter) return acc
    if (!acc[letter]) acc[letter] = []
    acc[letter].push(term)
    return acc
  }, {})

  const letters = Object.keys(groupedByLetter).sort()

  return (
    <div className="flex w-full h-full">
      {/* Master: Begriffsliste. Desktop = feste Spalte, Mobile = Vollbreite und
          ausgeblendet, sobald ein Begriff geöffnet ist. */}
      <aside
        className={`flex flex-col h-full overflow-hidden w-[380px] shrink-0 border-r border-sec30 bg-sec0 md:w-full md:border-r-0 ${
          activeTermId ? 'md:hidden' : ''
        }`}
      >
        <div className="p-4 flex flex-col gap-2 border-b border-sec30 shrink-0">
          <h1 className="text-h4 text-b100">{t('icuGlossary.title', 'Glossar')}</h1>
          <p className="text-textS text-b50">
            {t(
              'icuGlossary.intro',
              'Hier finden Sie viele medizinische Fachbegriffe, die Ihnen während des Krankenhausaufenthaltes Ihres Angehörigen begegnen könnten, verständlich und einfach erklärt.',
            )}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto py-3">
          {terms.length === 0 ? (
            <p className="px-3 text-textS text-b50">
              {t('icuGlossary.noResults', 'Keine Glossar-Begriffe verfügbar.')}
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {letters.map((letter) => (
                <div key={letter}>
                  <h2 className="pl-2 pr-3 text-textXS font-bold text-sec50 uppercase tracking-wide">
                    {letter}
                  </h2>
                  <div className="flex flex-col gap-2.5 mt-1 px-3">
                    {groupedByLetter[letter].map((term) => (
                      <button
                        key={term.id}
                        type="button"
                        className={`text-left w-full px-3.5 py-3 rounded-2xl border transition-all duration-200 ${
                          term.id === activeTermId
                            ? 'bg-b0 border-primary/40 shadow-[0_4px_16px_rgba(85,160,190,0.14)] ring-1 ring-primary/15'
                            : 'bg-b0 border-sec10/70 shadow-[0_2px_8px_rgba(0,0,0,0.05)] hover:border-sec30 hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]'
                        }`}
                        onClick={() =>
                          navigate(`/home/${patientId}/${paths.icuGlossary}/${term.id}`)
                        }
                      >
                        <div className="font-semibold text-sec100">{term.term}</div>
                        {term.layman_term && (
                          <div className="text-textS text-b50">{term.layman_term}</div>
                        )}
                        {term.short_description && (
                          <div className="text-textS text-b90 mt-1 line-clamp-2">
                            {term.short_description}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* Detail: ausgewählter Begriff. Mobile nur sichtbar, wenn geöffnet;
          Desktop zeigt sonst einen Hinweis. */}
      <section
        className={`flex-1 h-full overflow-y-auto flex justify-center px-8 py-6 md:px-4 md:py-4 ${
          activeTermId ? '' : 'md:hidden'
        }`}
      >
        {activeTermId ? (
          <Outlet />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center text-b50 max-w-content w-full">
            <p className="text-textBase">
              {t('icuGlossary.selectPrompt', 'Bitte wählen Sie links einen Begriff aus.')}
            </p>
          </div>
        )}
      </section>
    </div>
  )
}
